/**
 * RoadmapAttachments — image attachments panel in the roadmap editor modal.
 *
 * Sits between the detail body and the comment thread, and behaves like the
 * comment thread does: realtime onSnapshot listing, direct-to-Firestore reads,
 * mutations that funnel through `services/firebase`. Like comments, it needs a
 * saved doc to hang off, so the caller renders a placeholder for a brand-new
 * item rather than mounting this.
 *
 * Thumbnails render the FULL image scaled by CSS — no server-side thumbnail
 * pipeline. That keeps the whole feature client-side, at the cost of a grid of
 * 25 MB originals downloading at full weight. Acceptable for a super-admin-only
 * panel with a handful of images per item; if attachment counts ever grow, a
 * resize-on-upload step is the fix.
 */
import { useEffect, useRef, useState } from 'react';
import JSZip from 'jszip';
import toast from 'react-hot-toast';
import { Button } from '../common/Button';
import { ImageLightbox } from '../common/ImageLightbox';
import {
  ROADMAP_ATTACHMENT_ACCEPT,
  ROADMAP_ATTACHMENT_MAX_BYTES,
  deleteRoadmapAttachment,
  subscribeToRoadmapAttachments,
  uploadRoadmapAttachment,
} from '../../services/firebase';
import { formatAbsolute } from '../../utils/roadmapDates';
import type { RoadmapAttachment } from '../../types';

interface RoadmapAttachmentsProps {
  roadmapId: string;
  /** Used as the zip's basename. Falls back to the item id when blank. */
  itemTitle?: string;
}

/** Bytes to a short human string, e.g. '1.4 MB'. */
function formatSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Title → safe zip basename. Empty result means the caller should fall back. */
function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

/**
 * Trigger a browser download for a blob without a file-saver dependency.
 *
 * The object URL is revoked on a timeout rather than immediately: Safari has
 * historically cancelled the download if the URL dies in the same tick as the
 * synthetic click.
 */
function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function RoadmapAttachments({
  roadmapId,
  itemTitle,
}: RoadmapAttachmentsProps) {
  const [attachments, setAttachments] = useState<RoadmapAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [uploadingName, setUploadingName] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [zipping, setZipping] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [preview, setPreview] = useState<RoadmapAttachment | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!roadmapId) return;
    setLoading(true);
    setLoadError(false);
    const unsubscribe = subscribeToRoadmapAttachments(
      roadmapId,
      (rows) => {
        setAttachments(rows);
        setLoading(false);
      },
      () => {
        setLoadError(true);
        setLoading(false);
      },
    );
    return unsubscribe;
  }, [roadmapId]);

  const busy = uploadingName !== null || zipping || deletingId !== null;

  /**
   * Upload sequentially, not in parallel: the progress bar tracks one file at
   * a time, and a batch of 25 MB images going up at once mostly just starves
   * each other's bandwidth. A failure reports and stops the rest — the ones
   * already uploaded stay.
   */
  const handleFiles = async (files: FileList | File[]) => {
    const list = Array.from(files);
    if (list.length === 0) return;

    for (const file of list) {
      setUploadingName(file.name);
      setProgress(0);
      try {
        await uploadRoadmapAttachment(roadmapId, file, {
          onProgress: setProgress,
        });
        // onSnapshot delivers the row; nothing to set here.
      } catch (err) {
        console.error('Failed to upload roadmap attachment:', err);
        toast.error(
          err instanceof Error ? err.message : `Failed to upload ${file.name}`,
        );
        break;
      } finally {
        setUploadingName(null);
        setProgress(0);
      }
    }
  };

  const handleInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = e.target.files;
    if (files) await handleFiles(files);
    // Reset so re-picking the same file fires change again.
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    if (busy) return;
    const dropped = Array.from(e.dataTransfer.files ?? []);
    // A drag-drop bypasses the input's `accept`, so filter here too. The
    // service layer re-checks; this only avoids a toast per stray file.
    const images = dropped.filter((f) => f.type.startsWith('image/'));
    if (images.length === 0) {
      toast.error('Only images can be attached');
      return;
    }
    if (images.length < dropped.length) {
      toast.error(`Skipped ${dropped.length - images.length} non-image file(s)`);
    }
    await handleFiles(images);
  };

  const handleDelete = async (attachment: RoadmapAttachment) => {
    if (busy) return;
    if (!window.confirm(`Delete "${attachment.filename}"? This can't be undone.`)) {
      return;
    }
    setDeletingId(attachment.id);
    try {
      const result = await deleteRoadmapAttachment(roadmapId, attachment.id);
      if (result.storageDeleted) {
        toast.success('Attachment deleted');
      } else {
        // The row is gone either way — say so rather than reporting success
        // for a delete that left bytes behind.
        toast.success('Attachment removed (stored file was already missing)');
      }
    } catch (err) {
      console.error('Failed to delete roadmap attachment:', err);
      toast.error(
        err instanceof Error ? err.message : 'Failed to delete attachment',
      );
    } finally {
      setDeletingId(null);
    }
  };

  /**
   * Zip every attachment in the browser.
   *
   * Fetching the stored `downloadUrl` works cross-origin without any bucket
   * CORS config — the tokenised firebasestorage.googleapis.com endpoint serves
   * these permissively. A file that fails to fetch is skipped with a toast
   * rather than sinking the whole zip.
   */
  const handleDownloadAll = async () => {
    if (attachments.length === 0 || busy) return;
    setZipping(true);
    try {
      const zip = new JSZip();
      const used = new Set<string>();
      let failed = 0;

      for (const attachment of attachments) {
        try {
          const res = await fetch(attachment.downloadUrl);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const blob = await res.blob();

          // Two uploads of 'screenshot.png' would otherwise collide inside
          // the archive and silently drop one.
          let name = attachment.filename || `${attachment.id}`;
          if (used.has(name)) {
            const dot = name.lastIndexOf('.');
            const stem = dot > 0 ? name.slice(0, dot) : name;
            const ext = dot > 0 ? name.slice(dot) : '';
            name = `${stem}-${attachment.id.slice(0, 6)}${ext}`;
          }
          used.add(name);

          zip.file(name, blob);
        } catch (err) {
          console.error(`Failed to fetch attachment ${attachment.id}:`, err);
          failed += 1;
        }
      }

      if (used.size === 0) {
        toast.error('Could not fetch any attachments');
        return;
      }

      const blob = await zip.generateAsync({ type: 'blob' });
      const base = slugify(itemTitle ?? '') || roadmapId;
      saveBlob(blob, `${base}-attachments.zip`);

      if (failed > 0) {
        toast.error(`${failed} file(s) could not be included`);
      }
    } catch (err) {
      console.error('Failed to build attachments zip:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to build zip');
    } finally {
      setZipping(false);
    }
  };

  const maxMb = Math.round(ROADMAP_ATTACHMENT_MAX_BYTES / (1024 * 1024));

  return (
    <div className="border-t border-gray-200 pt-4 space-y-3">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-900">Attachments</h3>
        {!loading && !loadError && (
          <span className="text-xs text-gray-500">{attachments.length}</span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {attachments.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDownloadAll}
              disabled={busy}
            >
              {zipping ? 'Zipping…' : 'Download all'}
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
          >
            Attach image
          </Button>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={ROADMAP_ATTACHMENT_ACCEPT}
        multiple
        onChange={handleInputChange}
        className="hidden"
      />

      {uploadingName && (
        <div className="space-y-1">
          <div className="flex justify-between text-xs text-gray-600">
            <span className="truncate pr-2">Uploading {uploadingName}…</span>
            <span>{progress}%</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
            <div
              className="h-full bg-brand-600 transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (!busy) setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={handleDrop}
        className={`rounded-lg border-2 border-dashed p-3 transition-colors ${
          dragActive
            ? 'border-brand-500 bg-brand-50'
            : 'border-gray-200 bg-transparent'
        }`}
      >
        {loading ? (
          <div className="py-3 text-sm italic text-gray-500">
            Loading attachments…
          </div>
        ) : loadError ? (
          <div className="py-3 text-sm text-red-600">
            Couldn’t load attachments. Check your connection and reopen the item.
          </div>
        ) : attachments.length === 0 ? (
          <div className="py-3 text-sm italic text-gray-500">
            No attachments yet. Drop images here, or use “Attach image”. Up to{' '}
            {maxMb} MB each.
          </div>
        ) : (
          <ul className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-3">
            {attachments.map((attachment) => (
              <li key={attachment.id} className="group relative">
                <button
                  type="button"
                  onClick={() => setPreview(attachment)}
                  className="block w-full overflow-hidden rounded border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-500"
                  title={`Preview ${attachment.filename}`}
                >
                  <img
                    src={attachment.downloadUrl}
                    alt={attachment.filename}
                    loading="lazy"
                    className="h-[120px] w-full object-cover"
                  />
                </button>

                <div className="absolute right-1 top-1 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                  <a
                    href={attachment.downloadUrl}
                    download={attachment.filename}
                    target="_blank"
                    rel="noreferrer"
                    title="Download"
                    aria-label={`Download ${attachment.filename}`}
                    className="rounded bg-white/90 p-1 text-gray-700 shadow hover:bg-white"
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
                      />
                    </svg>
                  </a>
                  <button
                    type="button"
                    onClick={() => handleDelete(attachment)}
                    disabled={busy}
                    title="Delete"
                    aria-label={`Delete ${attachment.filename}`}
                    className="rounded bg-white/90 p-1 text-red-600 shadow hover:bg-white disabled:opacity-50"
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673A2.25 2.25 0 0 1 15.916 21.75H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397M4.772 5.79c-.34.059-.68.114-1.022.165m1.022-.165 1.05-.155M9.26 5.636V4.5a1.125 1.125 0 0 1 1.125-1.125h3.23c.621 0 1.125.504 1.125 1.125v1.136"
                      />
                    </svg>
                  </button>
                </div>

                {deletingId === attachment.id && (
                  <div className="absolute inset-0 flex items-center justify-center rounded bg-white/70 text-xs text-gray-600">
                    Deleting…
                  </div>
                )}

                <p
                  className="mt-1 truncate text-xs text-gray-700"
                  title={attachment.filename}
                >
                  {attachment.filename}
                </p>
                <p className="text-[11px] text-gray-400">
                  {formatSize(attachment.size)} ·{' '}
                  {formatAbsolute(attachment.uploadedAt)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ImageLightbox
        src={preview?.downloadUrl ?? null}
        filename={preview?.filename}
        onClose={() => setPreview(null)}
      />
    </div>
  );
}
